import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ValidatorFn, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { first, Observable, tap } from 'rxjs';
import { ActivityCodeEnum, CategoryEnum, PurposeEnum } from 'src/app/models/shared-models';
import { environment } from 'src/environments/environment';
import { BaseWizardComponent } from '../base-wizard.component';
import {
  ILotPlanModel, IGeneralInformationModel, LotPlanModel, ICategoryPurposeDataModel, ActivityStorageDataModel,
  IActivityStorageDataModel, ICategoryPurposeActivityDataModel, IPurposeModel
} from '../models/veg-wizard-models';
import { ActivityService } from '../services/activity-service';
import { UtilService } from '../services/util.service';
import { VegWizardDataService } from '../services/veg-wizard-data.service';

@UntilDestroy()
@Component({
  selector: 'app-select-activity',
  templateUrl: './select-activity.component.html',
  styleUrls: ['./select-activity.component.css'],
  imports: [
    CommonModule,
    ReactiveFormsModule
  ],
})
export class SelectActivityComponent extends BaseWizardComponent implements OnInit {

  form: FormGroup = new FormGroup({});
  lotOnPlan: ILotPlanModel = {} as ILotPlanModel;
  purpose: IPurposeModel | undefined;
  nextButtonClicked = false;

  get hasMeasuredDensityControl() { return this.form.controls['hasMeasuredDensity']; }
  get activityControl() { return this.form.controls['activity']; }
  get formInputNECValidControl() { return this.form.controls['formInputNECValid']; }
  get formInputFireManagementValidControl() { return this.form.controls['formInputFireManagementValid']; }
  get formInputInfrastructureCoastalRuleValidControl() { return this.form.controls['formInputInfrastructureCoastalRuleValid']; }

  purposeQuestionaire: IGeneralInformationModel = {
    promptQuestionMessage: '',
    userSelectsNoDisplayMessage: '',
    userSelectsYesDisplayMessage: '',
    generalInfoMessage: '',
    secondPromptQuestionMessage: '',
    secondUserSelectsNoDisplayMessage: '',
    secondUserSelectsYesDisplayMessage: '',
  };

  activityQuestionaire: IGeneralInformationModel = {
    promptQuestionMessage: '',
    userSelectsNoDisplayMessage: '',
    userSelectsYesDisplayMessage: '',
    generalInfoMessage: '',
    secondPromptQuestionMessage: '',
    secondUserSelectsNoDisplayMessage: '',
    secondUserSelectsYesDisplayMessage: '',
  };

  activityCheckboxPrefix = 'activity-'
  activityData: IActivityStorageDataModel = new ActivityStorageDataModel();
  categoryData: ICategoryPurposeDataModel[] = [];
  allowMultipleActivities = false;
  requiresMeasuredDensity = false;
  availableActivities: ICategoryPurposeActivityDataModel[] = [];
  distinctActivities: ICategoryPurposeActivityDataModel[] = [];
  showAirstripOrHelipadMessage = false;
  purposeCode = '';
  showAgriculturalEfficiencyMessage = false;
  showActivityValidForNecessaryEnvironmentalClearingError = false;
  showFireManagementLinesValidError = false;
  showInfrastructureCoastalRulesError = false;
  formControlActivityNames: string[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private vwds: VegWizardDataService,
    private activityService: ActivityService,
    private utilService: UtilService,
    router: Router) {
    super();
  }

  ngOnInit(): void {
    this.activityData = this.vwds.getActivities() ?? new ActivityStorageDataModel();
    this.categoryData = this.vwds.getCategories();
    this.lotOnPlan = this.vwds.getLotPlanModel() || {} as ILotPlanModel;
    this.purpose = this.vwds.getPurpose()?.purpose;
    this.purposeCode = this.vwds.getPurpose()?.purpose.purposeCode ?? '';
    this.allowMultipleActivities = this.categoryData.some(category => category.allowMultipleActivities);
    this.requiresMeasuredDensity = this.categoryData.some(category => category.requiresMeasuredDensity);

    this.form = this.formBuilder.group({
      hasMeasuredDensity: [this.activityData.hasMeasuredDensity, this.hasMeasuredDensityValidator()],
      activity: [this.activityData.activity],

      // input controls to capture form related validations / errors
      formInputNECValid: null,
      formInputFireManagementValid: null,
      formInputInfrastructureCoastalRuleValid: null
    });

    this.activityQuestionaire = this.activityService.getActivityMessage(this.purpose);

    this.form.setValidators([this.activitySelectValidator(), this.hasMeasuredDensityValidator()]);

    this.setActivitiesRefData$()
      .pipe(first(), untilDestroyed(this))
      .subscribe(() => {
        this.setCheckboxControls();
      });

    this.form.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.activityData.selectedActivities = this.allowMultipleActivities
          ? this.availableActivities.filter(activity =>
            this.getSelectedActivityCodes().find(activityCode => activityCode === activity.activityCode))
          : this.availableActivities.filter(activity => this.activityControl?.value === activity.activityCode);

        this.activityData.hasMeasuredDensity = this.hasMeasuredDensityControl?.value;
        this.activityData.activity = this.activityControl?.value;

        this.showAirstripOrHelipadMessage = this.activityData.selectedActivities?.some(x => x.activityCode === ActivityCodeEnum.airstripOrHelipads);
        this.showAgriculturalEfficiencyMessage = this.purposeCode === PurposeEnum.agriculturalEfficiency;

        // show errors if not valid
        this.showActivityValidForNecessaryEnvironmentalClearingError = !this.isActivityValidForNecessaryEnvironmentalClearing();
        this.showFireManagementLinesValidError = !this.isFireManagementLinesValid();
        this.showInfrastructureCoastalRulesError = !this.isInfrastructureCoastalRuleValid();

      });

  }

  private setActivitiesRefData$(): Observable<ICategoryPurposeActivityDataModel[]> {
    return this.activityService.getActivitiesForCategoryAndPurpose$(this.categoryData.map(category =>
      category.categoryCode), this.purposeCode ?? '')
      .pipe(
        tap((result: ICategoryPurposeActivityDataModel[]) => {
          this.availableActivities = result;
          this.distinctActivities = [...new Map(this.availableActivities.map(activity =>
            [activity['activityCode'], activity])).values()];
        })
      );
  }

  private setCheckboxControls() {
    const existingSelectedActivities = this.utilService.clone(this.activityData.selectedActivities);

    this.formControlActivityNames = [];
    for (const activity of this.distinctActivities) {

      let defaultCheckBoxValue = false;

      if (!!existingSelectedActivities && existingSelectedActivities.length > 0) {
        if (existingSelectedActivities.some(selectedActivity => selectedActivity.activityCode == activity.activityCode)) {
          defaultCheckBoxValue = true;
        }
      }

      const activityNameCode = `${this.activityCheckboxPrefix}${activity.activityCode}`
      this.form.addControl(activityNameCode, new FormControl(defaultCheckBoxValue));
      this.formControlActivityNames.push(activityNameCode);
    }

  }

  private getSelectedActivityCodes() {
    const formValue = this.form.getRawValue();
    const selectedActivityCodes: string[] = [];

    for (const prop in formValue) {
      if (!formValue.hasOwnProperty(prop)) { continue; }
      if (!prop.startsWith(this.activityCheckboxPrefix)) { continue; }
      if (!formValue[prop]) { continue; }

      selectedActivityCodes.push(prop.replace(this.activityCheckboxPrefix, ''));
    }
    return selectedActivityCodes;
  }

  activitySelectValidator(): ValidatorFn {
    return () => {
      const selectedActivities = this.getSelectedActivityCodes();
      return (this.allowMultipleActivities
        ? selectedActivities.length
        : this.activityControl?.value) ? null : { activityRequiredValidationError: true };
    };
  }

  hasMeasuredDensityValidator(): ValidatorFn {
    return () => {
      return this.requiresMeasuredDensity
        ? ((this.form.get('hasMeasuredDensity')?.value)
          ? null : { measuredDensityRequiredValidationError: true })
        : null;
    }
  }

  isActivityValidForNecessaryEnvironmentalClearing(): boolean {

    const isNecessaryEnvironmentalClearingPurposeSelected = (this.purposeCode === PurposeEnum.necessaryEnvironmentalClearing);
    const isCategoryBSelected = this.categoryData.some(category => category.categoryCode === CategoryEnum.catB);
    const isChannelDiversionActivitySelected = (this.activityData.activity === ActivityCodeEnum.channelDiversion
      || (this.activityData?.selectedActivities?.some(x => x.activityCode === ActivityCodeEnum.channelDiversion) ?? false));

    let isValidResult = true;
    if (isNecessaryEnvironmentalClearingPurposeSelected && isCategoryBSelected && isChannelDiversionActivitySelected) {
      isValidResult = false;
    }

    this.formInputNECValidControl?.setErrors(null);
    if (!isValidResult)
      this.formInputNECValidControl?.setErrors({ 'isActivityValidForNecessaryEnvironmentalClearing Required valid': true });

    return isValidResult
  }

  isFireManagementLinesValid(): boolean {

    let isValidResult = true;
    if (this.categoryData.some(category => category.categoryCode === CategoryEnum.catB)) {
      isValidResult = !(this.activityData.selectedActivities?.some(a => a.activityCode === ActivityCodeEnum.fireManagementLines))
    }

    this.formInputFireManagementValidControl?.setErrors(null);
    if (!isValidResult)
      this.formInputFireManagementValidControl?.setErrors({ 'isFireManagementLinesValid Required valid': true });

    return isValidResult
  }

  isInfrastructureCoastalRuleValid(): boolean {
    let isValidResult = true;

    this.formInputInfrastructureCoastalRuleValidControl?.setErrors(null);

    if (this.lotOnPlan.isCoastalLotPlanBioregion
      && this.purpose?.purposeCode === PurposeEnum.infrastructure
      && this.activityControl.value == ActivityCodeEnum.firebreaks) {
      isValidResult = false;
      this.formInputInfrastructureCoastalRuleValidControl?.setErrors({ 'blockProgressionInfrastructureCoastalRules not valid': true });
    }

    return isValidResult
  }

  override next(): void {
    this.form.markAsDirty()
    this.form.updateValueAndValidity();
    this.nextButtonClicked = true;

    if (!this.form.valid) {
      this.utilService.getFormValidationErrors(this.form);
      return;
    }

    this.vwds.setActivities$(this.activityData)
      .pipe(first(), untilDestroyed(this))
      .subscribe(() => super.next());
  }

  override cancel(): void {
    super.cancel();
    this.vwds.clear().pipe(first(), untilDestroyed(this)).subscribe();
  }
}
